import * as EXIF from 'exif-js';

interface CanvasProcessingResult extends HTMLCanvasElement {
    _area: { top: number, left: number, width: number, height: number } 
}

export class ThumbnailFactory {
  public static create(source: string | Blob, mimeType: string | number, maxWidth: number, maxHeight?: number | HTMLCanvasElement, canvas?: HTMLCanvasElement): Promise<HTMLCanvasElement> {

    let _mimeType: string;
    let _maxWidth: number;
    let _maxHeight: number;
    let _canvas: HTMLCanvasElement;
    ;
    if ((<Blob>source).type != undefined) {
      _mimeType = (<Blob>source).type;
      _maxWidth = <number>mimeType;
      _maxHeight = maxWidth;
      _canvas = <HTMLCanvasElement>maxHeight;
    } else {
      _mimeType = <string>mimeType;
      _maxWidth = <number>maxWidth;
      _maxHeight = <number>maxHeight;
      _canvas = <HTMLCanvasElement>canvas;
    }

    return new Promise<HTMLCanvasElement>((_resolve, reject) => {
      ThumbnailFactory.loadData(source)
        .then((data) => {
          if (_mimeType.startsWith('image/')) {
            return ThumbnailFactory.createImageFromData(data, _mimeType);
          } else if (_mimeType.startsWith('video/')) {
            return ThumbnailFactory.createVideoFromData(data, _mimeType);
          } else {
            reject('Unsupported mime-type');
          }
        })
        .then((imageAndorientation: { source: HTMLImageElement | HTMLVideoElement, orientation: number }) => {
          return ThumbnailFactory.createThumbnailFromSource(imageAndorientation, _canvas, _maxWidth, _maxHeight);
        });
    });
  }

  private static loadData(source: string | Blob): Promise<ArrayBuffer> {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      if ((<Blob>source).type != undefined) {
        let fileReader = new FileReader();
        fileReader.onload = () => {
          resolve(<ArrayBuffer>fileReader.result);
        }
        fileReader.onerror = () => {
          reject('Unable to read source.');
        }
        fileReader.readAsArrayBuffer(<Blob>source);
      } else {
        let http = new XMLHttpRequest();
        http.onload = () => {
          if (http.status == 200 || http.status === 0) {
            resolve(<ArrayBuffer>http.response);
          } else {
            reject('Unable to load source.');
          }
          http = null;
        };
        http.onerror = () => {
          reject('Unable to load source.');
        }
        http.open("GET", <string>source, true);
        http.responseType = "arraybuffer";
        http.send(null);
      }
    });
  }

  private static createImageFromData(data: ArrayBuffer, type: string): Promise<{ source: HTMLImageElement | HTMLVideoElement, orientation: number }> {
    return new Promise<{ source: HTMLImageElement | HTMLVideoElement, orientation: number }>((resolve, reject) => {
      let exif = EXIF.readFromBinaryFile(data);
      let blob = new Blob([data], { type: type });
      let urlCreator = window.URL || window.webkitURL;
      let imageUrl = urlCreator.createObjectURL(blob);
      let image = new Image();
      image.onload = () => {
        resolve({ source: image, orientation: exif && exif.Orientation ? exif.Orientation : 1 });
      }
      image.onerror = () => {
        reject('Unsupported image format');
      }
      image.src = imageUrl;
    });
  }

  private static createVideoFromData(data: ArrayBuffer, type: string): Promise<{ source: HTMLImageElement | HTMLVideoElement, orientation: number }> {
    return new Promise<{ source: HTMLImageElement | HTMLVideoElement, orientation: number }>((resolve, reject) => {
      let blob = new Blob([data], { type: type });
      let urlCreator = window.URL || window.webkitURL;
      let videoUrl = urlCreator.createObjectURL(blob);
      let video = document.createElement('video');
      video.onloadedmetadata = () => {
        video.addEventListener('timeupdate', function() {
          let orientation = video.videoWidth > video.videoHeight ? 1 : 6;
          video.width = orientation < 5 ? video.videoWidth : video.videoHeight;
          video.height = orientation < 5 ? video.videoHeight : video.videoWidth;
          resolve({ source: video, orientation: orientation });
        });
        video.currentTime = 0;
      };
      video.onerror = function() {
        reject('Unsupported video format');
      }
      video.src = videoUrl;
    });
  }

  private static createThumbnailFromSource(sourceAndOrientation: { source: HTMLImageElement | HTMLVideoElement, orientation: number }, destination: HTMLCanvasElement, maxWidth: number, maxHeight: number): HTMLCanvasElement {
    let sourceWidth = sourceAndOrientation.orientation < 5 ? sourceAndOrientation.source.width : sourceAndOrientation.source.height;
    let sourceHeight = sourceAndOrientation.orientation < 5 ? sourceAndOrientation.source.height : sourceAndOrientation.source.width;

    let hGain = maxHeight >= 0 ? maxHeight / sourceHeight : undefined;
    let vGain = maxWidth >= 0 ? maxWidth / sourceWidth : undefined;
    let gain = hGain ? vGain ? Math.min(hGain, vGain) : hGain : vGain ? vGain : 1.0;

    //resize
    let resizeCanvas = ThumbnailFactory.resizeImage(sourceAndOrientation.source, gain);

    //fix orientation
    let orientationCanvas = ThumbnailFactory.fixImageOrientation(resizeCanvas, resizeCanvas._area, sourceAndOrientation.orientation);
    resizeCanvas.width = 0; //free memory
    resizeCanvas.height = 0;

    //crop
    let cropCanvas = ThumbnailFactory.cropImage(orientationCanvas, orientationCanvas._area, destination);
    orientationCanvas.width = 0; //free memory
    orientationCanvas.height = 0;
    return cropCanvas;

  }

  private static resizeImage(source: HTMLImageElement | HTMLVideoElement, gain: number, destination?: HTMLCanvasElement):  CanvasProcessingResult{
    //source: https://stackoverflow.com/questions/17861447/html5-canvas-drawimage-how-to-apply-antialiasing
    let canvas = <CanvasProcessingResult> (destination != null ? destination : document.createElement('canvas') );
    let ctx = canvas.getContext('2d');

    let prevWidth = source.width,
      prevHeight = source.height;
    let curWidth, curHeight;

    let targetWidth = prevWidth * gain,
      targetHeight = prevHeight * gain;

    canvas._area = {
      top: 0,
      left: 0,
      width: targetWidth,
      height: targetHeight
    };

    if (gain < 1.0) {
      curWidth = Math.max(prevWidth * 0.5, targetWidth);
      curHeight = Math.max(prevHeight * 0.5, targetHeight);
      canvas.width = curWidth;
      canvas.height = curHeight;
      ctx.drawImage(source, 0, 0, curWidth, curHeight);
      let imageData;
      //we cannot reuse the canvas over and over because images containing an alpha channel will bleed through
      let helperCanvas = null;
      let helperctx = null;

      while (curWidth > targetWidth) {
        prevWidth = curWidth;
        prevHeight = curHeight;
        curWidth = Math.max(prevWidth * 0.5, targetWidth);
        curHeight = Math.max(prevHeight * 0.5, targetHeight);
        imageData = ctx.getImageData(0, 0, prevWidth, prevHeight);
        if (helperCanvas == null) {
          helperCanvas = document.createElement('canvas');
          helperctx = helperCanvas.getContext('2d');
          helperCanvas.width = prevWidth;
          helperCanvas.height = prevHeight;
        }
        helperctx.clearRect(0, 0, prevWidth, prevHeight);
        helperctx.putImageData(imageData, 0, 0);
        ctx.clearRect(0, 0, curWidth, curHeight);
        ctx.drawImage(helperCanvas, 0, 0, prevWidth, prevHeight, 0, 0, curWidth, curHeight);
      }
      if (helperCanvas) {
        helperCanvas.width = 0;
        helperCanvas.height = 0;
        helperCanvas = null;
      }
    } else {
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    }
    return canvas;
  }

  private static fixImageOrientation(source: HTMLCanvasElement, area: { top: number, left: number, width: number, height: number }, orientation: number, destination?: HTMLCanvasElement): CanvasProcessingResult {
    let canvas = <CanvasProcessingResult>(destination != null ? destination : document.createElement('canvas') );
    let ctx = canvas.getContext('2d');
    let size = Math.max(area.width, area.height);

    canvas.width = size;
    canvas.height = size;
    canvas._area = {
      top: 0,
      left: 0,
      width: orientation < 5 ? area.width : area.height,
      height: orientation < 5 ? area.height : area.width
    };

    switch (orientation) {
      case 2:
        // horizontal flip
        ctx.translate(area.width, 0);
        ctx.scale(-1, 1);
        break;
      case 3:
        // 180° rotate left
        ctx.translate(area.width, area.height);
        ctx.rotate(Math.PI);
        break;
      case 4:
        // vertical flip
        ctx.translate(0, area.height);
        ctx.scale(1, -1);
        break;
      case 5:
        // vertical flip + 90 rotate right
        ctx.rotate(0.5 * Math.PI);
        ctx.scale(1, -1);
        break;
      case 6:
        // 90° rotate right
        ctx.rotate(0.5 * Math.PI);
        ctx.translate(0, -area.height);
        break;
      case 7:
        // horizontal flip + 90 rotate right
        ctx.rotate(0.5 * Math.PI);
        ctx.translate(area.width, -area.height);
        ctx.scale(-1, 1);
        break;
      case 8:
        // 90° rotate left
        ctx.rotate(-0.5 * Math.PI);
        ctx.translate(-area.width, 0);
        break;
    }

    ctx.drawImage(source, area.left, area.top, area.width, area.height, area.left, area.top, area.width, area.height);

    return canvas;
  }

  private static cropImage(source: HTMLCanvasElement, area: { top: number, left: number, width: number, height: number }, destination: HTMLCanvasElement): CanvasProcessingResult {
    let canvas = <CanvasProcessingResult>(destination != null ? destination : document.createElement('canvas') );
    let ctx = canvas.getContext('2d');

    canvas.width = area.width;
    canvas.height = area.height;
    canvas.style.width = '' + area.width + 'px';
    canvas.style.height = '' + area.height + 'px';
    canvas._area = {
      top: 0,
      left: 0,
      width: area.width,
      height: area.height
    };

    ctx.drawImage(source, area.left, area.top, area.width, area.height, area.left, area.top, area.width, area.height);

    return canvas;
  }


}
